/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core'
import { ListHelperService, MyProfileListMode, MyProfilePhoneFormService, PhoneNumberService } from '@myprofile-ui/myprofile-phone-utils'
import { PidxLoaderModeEnum, PidxPdsModalWrapperService } from '@myprofile-ui/pidx-ui-components'
import { WINDOW } from '@myprofile-ui/pidx-utils'
import { TranslateService } from '@ngx-translate/core'
import { Store } from '@ngxs/store'
import { Observable, Subject, combineLatest } from 'rxjs'
import { filter, map, pairwise, startWith, take, takeUntil } from 'rxjs/operators'
import { DeletePhone, ShowBlankEditor } from '../../store/editor.actions'
import { SetPhoneBeingEdited, SetPhoneBeingVerified } from '../../store/list.actions'
import { SetAppErrorMessage, SetEditorErrorMessage } from '../../store/phone-widget.actions'
import { MyProfilePhoneEditFormComponent } from '../myprofile-phone-editform/myprofile-phone-editform.component'
import { PhoneCommunicationDto } from '../../../../../../apps/myprofile-ui/src/app/services/phone/phone.service'

@Component({
  selector: 'myprofile-phone-list',
  templateUrl: './myprofile-phone-list.component.html',
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfilePhoneListComponent implements OnInit, OnDestroy {
  private window = inject<Window>(WINDOW)

  private store = inject(Store)
  private listHelperService = inject(ListHelperService)
  private phoneFormService = inject(MyProfilePhoneFormService)
  private translateService = inject(TranslateService)
  private pidxPdsModalWrapperService = inject(PidxPdsModalWrapperService)
  private phoneNumberService = inject(PhoneNumberService)
  private elementRef = inject(ElementRef)

  @Input() mode: MyProfileListMode = MyProfileListMode.ALL
  @Input() hasConfirmedVehicle = false
  @Input() isCnUser = false
  @Input() restrictGlobalUIChanges = false
  @Output() goToChangePorscheIdPageEvent: EventEmitter<any> = new EventEmitter<any>()
  @Output() verificationSuccessful: EventEmitter<any> = new EventEmitter<any>()

  listHasLoadingOverlay$: Observable<boolean> = inject(Store).select(({ phoneWidget }) => phoneWidget.phoneList.loading)
  phoneNumberBeingVerified$: Observable<{ number: string; section: MyProfileListMode }> = inject(Store).select(
    ({ phoneWidget }) => phoneWidget.phoneNumberBeingVerified,
  )
  phoneIdBeingEdited$: Observable<string | null> = inject(Store).select(({ phoneWidget }) => phoneWidget.phoneIdBeingEdited)
  public isEditMode = false
  public isLastMobileNumber = false
  public isVerificationMode = false
  public showListItem = true
  public unsubscribe$ = new Subject<void>()
  @ViewChildren(MyProfilePhoneEditFormComponent, { read: ElementRef }) private phoneEditFormQL!: QueryList<ElementRef>

  protected country = this.store.selectSnapshot((state) => state.phoneWidget.country)
  private _totalPhones: PhoneCommunicationDto[] = []
  private _phones: PhoneCommunicationDto[] = []

  get phones() {
    return this._phones
  }

  @Input()
  set phones(values: PhoneCommunicationDto[]) {
    this._totalPhones = values
    values = this.listHelperService.getItemsAccordingToListMode(this.mode, values)
    this._phones = values
  }

  private _phoneBeingEdited!: PhoneCommunicationDto | null | undefined

  get phoneBeingEdited() {
    return this._phoneBeingEdited
  }

  @Input()
  set phoneBeingEdited(value: PhoneCommunicationDto | null | undefined) {
    this._phoneBeingEdited = value
    this.isEditMode = ListHelperService.isEditMode(this.mode, this.phoneBeingEdited)
  }

  ngOnInit() {
    combineLatest([this.phoneIdBeingEdited$, this.phoneNumberBeingVerified$])
      .pipe(
        takeUntil(this.unsubscribe$),
        map(([phoneIdBeingEdited, phoneNumberBeingVerified]) => {
          return !phoneIdBeingEdited && phoneNumberBeingVerified && phoneNumberBeingVerified.section === this.mode
        }),
      )
      .subscribe((isVerificationMode) => {
        this.isVerificationMode = isVerificationMode
        this.showListItem = !isVerificationMode
      })

    this.setupFocusRestoreWhenEditorCloses()
  }

  private setupFocusRestoreWhenEditorCloses() {
    this.phoneIdBeingEdited$
      .pipe(
        startWith(null as string | null),
        pairwise(),
        filter(([prev, curr]) => prev != null && curr === null),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(([prevId]) => {
        this.window.setTimeout(() => {
          let btn = this.window.document.getElementById(`edit-phone-btn-${prevId}`) as HTMLElement | null
          if (!btn) {
            btn = this.elementRef.nativeElement.querySelector('profile-pds-p-button-pure[icon="edit"]') as HTMLElement | null
          }
          btn?.focus()
        }, 0)
      })
  }

  getFormattedPhoneForAria(phone: PhoneCommunicationDto): string {
    return this.phoneNumberService.getFormattedPhoneNumber(phone.number ?? '', this.country)
  }

  public editItem(item: PhoneCommunicationDto) {
    this.store.dispatch(new SetPhoneBeingEdited(item.id)).subscribe()
    this.phoneEditFormQL.changes.pipe(take(1)).subscribe((ql) => {
      this.window.setTimeout(() => {
        const host = ql.first?.nativeElement as HTMLElement
        host?.focus()
      }, 0)
    })
  }

  public deleteItem(item: PhoneCommunicationDto) {
    this.isLastMobileNumber = this.phoneFormService.isLastMobileNumber(item, this._totalPhones)
    this.prepareDeleteItem(item, this.isLastMobileNumber)
  }

  public cancelVerification() {
    this.store.dispatch(new SetPhoneBeingVerified(null))
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  public prepareDeleteItem(item: PhoneCommunicationDto, isLastPhoneNumber: boolean) {
    if (isLastPhoneNumber && item.isVerified) {
      this.showDeleteLastPhoneModal()
    } else if (item.isStandard && item.isVerified) {
      this.showDeleteStandardPhoneModal()
    } else {
      this.showDeletePromptModal(item)
    }
  }

  private async showDeleteLastPhoneModal() {
    const body = this.translateService.instant('pidpaw.deletePhone.error.deleteLast')
    const btnPrimary = this.translateService.instant('pidpaw.deletePhone.button.ok')

    await this.pidxPdsModalWrapperService.openModal({ body, btnPrimary })
  }

  private async showDeleteStandardPhoneModal() {
    const title = this.translateService.instant('pidp.deletePhone.error.deleteStandard.headline')
    const body = this.translateService.instant('pidp.deletePhone.error.deleteStandard.body')
    const btnPrimary = this.translateService.instant('pidpaw.deletePhone.error.deleteStandard.add')
    const btnGhost = this.translateService.instant('pidpaw.editPhone.deleteConfirmationModal.btnGhost')

    const result = await this.pidxPdsModalWrapperService.openModal({ title, body, btnPrimary, btnGhost })
    if (result) {
      this.store.dispatch(new SetAppErrorMessage(null))
      this.store.dispatch(new SetEditorErrorMessage(null))
      this.store.dispatch(new ShowBlankEditor(true)).subscribe()
      this.phoneEditFormQL.changes.pipe(take(1)).subscribe((ql) => {
        this.window.setTimeout(() => {
          const host = ql.first?.nativeElement as HTMLElement
          host?.focus()
        }, 0)
      })
    }
  }

  private async showDeletePromptModal(item: PhoneCommunicationDto) {
    const title = this.translateService.instant('pidpaw.editPhone.deleteConfirmationModal.title')
    // Wrap phone number with Unicode bidirectional isolation to ensure correct display in RTL languages
    const body = `\u2066${item.number}\u2069`
    const btnPrimary = this.translateService.instant('pidpaw.editPhone.deleteConfirmationModal.btnPrimary')
    const btnGhost = this.translateService.instant('pidpaw.editPhone.deleteConfirmationModal.btnGhost')

    const result = await this.pidxPdsModalWrapperService.openModal({ title, body, btnPrimary, btnGhost })
    if (result) {
      this.store.dispatch(new DeletePhone(item))
    }
  }

  private setFocusOnElementId(id: string) {
    this.window.setTimeout(() => {
      this.window.document.getElementById(id)?.focus()
    }, 0)
  }

  public goToChangePorscheIdPage() {
    this.goToChangePorscheIdPageEvent.emit()
  }

  protected readonly PidxLoaderModeEnum = PidxLoaderModeEnum
}
